"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.parseSchematic = parseSchematic;
exports.preParseSchematicSheets = preParseSchematicSheets;
const project_1 = require("./project");
const v6schema_1 = require("./schematic/v6schema");
function parseSchematic(def, parser, project, sch, resolver, netRefCtx, netCombobulationOpts, extractedImages) {
    if (sch.contents.startsWith('EESchema')) {
        throw new Error('EESchema schematics are not. Update your KiCad version.');
    }
    else {
        return (0, v6schema_1.parseSchematic)(def, parser, project, sch, resolver, netRefCtx, netCombobulationOpts, extractedImages);
    }
}
async function preParseSchematicSheets(def, parser, warnings) {
    const project = Object.values(def.other ?? {}).find((f) => f.type === 'project');
    const configuredProjectName = project ? schematicProjectName(project.originalFileName) : undefined;
    const { modernFiles: parsedFiles, legacySheets } = await parseSchematicFiles(def, parser, warnings, configuredProjectName);
    const rootFiles = getRootFiles(parsedFiles, project?.originalFileName);
    const outSheets = [...legacySheets];
    const pendingSheets = [];
    const expandedPaths = new Set();
    const reachedFiles = new Set();
    const rootUuidCounts = new Map();
    for (const rootFile of rootFiles) {
        rootUuidCounts.set(rootFile.mainSheet.uuid, (rootUuidCounts.get(rootFile.mainSheet.uuid) ?? 0) + 1);
        const rootPath = (0, project_1.joinKicadInstancePath)('', rootFile.mainSheet.uuid);
        const root = {
            ...rootFile.mainSheet,
            instancePath: rootPath,
            annotationPath: rootPath,
            netPath: '/',
            projectName: configuredProjectName ?? schematicProjectName(rootFile.mainSheet.filename),
        };
        pendingSheets.push({ sheet: root });
    }
    const cycleWarnings = new Set();
    let pendingIndex = 0;
    while (pendingIndex < pendingSheets.length || !project) {
        if (pendingIndex >= pendingSheets.length) {
            const unreachableEntry = [...parsedFiles.entries()]
                .filter(([filename]) => !reachedFiles.has(filename))
                .sort(([left], [right]) => left.localeCompare(right))[0];
            if (!unreachableEntry) {
                break;
            }
            const [unreachableFilename, unreachableFile] = unreachableEntry;
            const rootPath = (0, project_1.joinKicadInstancePath)('', unreachableFile.mainSheet.uuid);
            rootUuidCounts.set(unreachableFile.mainSheet.uuid, (rootUuidCounts.get(unreachableFile.mainSheet.uuid) ?? 0) + 1);
            pendingSheets.push({
                sheet: {
                    ...unreachableFile.mainSheet,
                    instancePath: rootPath,
                    annotationPath: rootPath,
                    netPath: '/',
                    projectName: schematicProjectName(unreachableFilename),
                },
            });
            warnings.push(`Sheet ${unreachableFilename} is unreachable from any root; adding it as a root`);
            continue;
        }
        const pending = pendingSheets[pendingIndex];
        pendingIndex += 1;
        const { sheet } = pending;
        const { instancePath } = sheet;
        if (!instancePath) {
            continue;
        }
        const expansionKey = `${sheet.filename}:${instancePath}`;
        if (expandedPaths.has(expansionKey)) {
            continue;
        }
        expandedPaths.add(expansionKey);
        reachedFiles.add(sheet.filename);
        outSheets.push(sheet);
        const sourceFile = parsedFiles.get(sheet.filename);
        if (!sourceFile) {
            continue;
        }
        for (const templates of childTemplatesBySymbol(sourceFile.childSheets)) {
            const activeTemplates = selectProjectTemplates(templates, sheet.projectName);
            const parentAnnotationPath = sheet.annotationPath ?? instancePath;
            const matchingTemplate = activeTemplates.find((childTemplate) => childTemplate.parentInstancePath === parentAnnotationPath);
            const childTemplate = matchingTemplate ?? activeTemplates[0];
            if (!childTemplate) {
                continue;
            }
            if (hasAncestorFile(pending, childTemplate.filename)) {
                const warning = `Sheet hierarchy contains a cycle at ${childTemplate.filename}`;
                if (!cycleWarnings.has(warning)) {
                    warnings.push(warning);
                    cycleWarnings.add(warning);
                }
                continue;
            }
            const childAnnotationPath = matchingTemplate?.annotationPath ?? (0, project_1.joinKicadInstancePath)(parentAnnotationPath, childTemplate.uuid);
            const legacySheetInstance = legacySheetInstanceForPath(sheet.legacySheetInstances, childAnnotationPath);
            if (!matchingTemplate && !legacySheetInstance) {
                warnings.push(`Sheet ${sheet.filename} has no child instance matching ${parentAnnotationPath} for ${childTemplate.filename}; expanding symbol`);
            }
            const occurrenceSegment = (childTemplate.sourceSymbolOccurrence ?? 1) > 1 ?
                `${childTemplate.uuid}~${childTemplate.sourceSymbolOccurrence}`
                : childTemplate.uuid;
            const childInstancePath = (0, project_1.joinKicadInstancePath)(instancePath, occurrenceSegment);
            const childFile = parsedFiles.get(childTemplate.filename);
            pendingSheets.push({
                sheet: {
                    ...childTemplate,
                    instancePath: childInstancePath,
                    annotationPath: childAnnotationPath,
                    parentInstancePath: instancePath,
                    projectName: sheet.projectName,
                    netPath: `${sheet.netPath}${childTemplate.name}/`,
                    contents: childFile?.mainSheet.contents,
                    libContents: childFile?.mainSheet.libContents,
                    legacySymbolInstances: sheet.legacySymbolInstances,
                    legacySheetInstances: sheet.legacySheetInstances,
                    page: matchingTemplate?.page ?? legacySheetInstance?.page,
                },
                parent: pending,
            });
        }
    }
    for (const [filename] of parsedFiles) {
        if (reachedFiles.has(filename)) {
            continue;
        }
        if (project) {
            const projectRoot = rootFiles[0]?.mainSheet.filename ?? '<unknown>';
            warnings.push(`Sheet ${filename} is unreachable from project root ${projectRoot}, skipping`);
        }
    }
    for (const [uuid, count] of rootUuidCounts) {
        if (count > 1) {
            warnings.push(`Sheet roots share UUID ${uuid}; retaining each root file as a separate design`);
        }
    }
    const childUuidCounts = new Map();
    const childInstanceIdentityCounts = new Map();
    for (const sheet of outSheets) {
        if (sheet.parentInstancePath) {
            const sourceUuid = sheet.sourceSheetUuid ?? sheet.uuid;
            childUuidCounts.set(sourceUuid, (childUuidCounts.get(sourceUuid) ?? 0) + 1);
            if (sheet.instancePath) {
                const identity = `${sourceUuid}:${sheet.instancePath}`;
                childInstanceIdentityCounts.set(identity, (childInstanceIdentityCounts.get(identity) ?? 0) + 1);
            }
        }
    }
    const allocatedDesignIds = new Set();
    for (const sheet of outSheets) {
        const sheetUuid = sheet.sourceSheetUuid ?? sheet.uuid;
        if (sheet.instancePath && sheet.parentInstancePath && (childUuidCounts.get(sheetUuid) ?? 0) > 1) {
            const identity = `${sheetUuid}:${sheet.instancePath}`;
            const idPath = (childInstanceIdentityCounts.get(identity) ?? 0) > 1 ?
                `${sheet.filename}:${sheet.instancePath}`
                : sheet.instancePath;
            sheet.uuid = kicadSheetInstanceDesignId(sheetUuid, idPath);
        }
        if (allocatedDesignIds.has(sheet.uuid)) {
            const instanceIdentity = sheet.instancePath ?? sheet.filename;
            sheet.uuid = kicadSheetInstanceDesignId(sheetUuid, instanceIdentity);
            if (allocatedDesignIds.has(sheet.uuid)) {
                sheet.uuid = kicadSheetInstanceDesignId(sheetUuid, `${sheet.filename}:${instanceIdentity}`);
            }
            let collisionIndex = 2;
            while (allocatedDesignIds.has(sheet.uuid)) {
                sheet.uuid = kicadSheetInstanceDesignId(sheetUuid, `${sheet.filename}:${instanceIdentity}:${collisionIndex}`);
                collisionIndex += 1;
            }
            warnings.push(`Sheet ${sheet.filename} design ID ${sheetUuid} collides with another design; assigned ${sheet.uuid}`);
        }
        allocatedDesignIds.add(sheet.uuid);
    }
    const sourceInstanceCounts = new Map();
    for (const sheet of outSheets) {
        sourceInstanceCounts.set(sheet.filename, (sourceInstanceCounts.get(sheet.filename) ?? 0) + 1);
    }
    for (const sheet of outSheets) {
        sheet.sourceInstanceCount = sourceInstanceCounts.get(sheet.filename);
    }
    assignSheetPages(outSheets, warnings);
    return outSheets;
}
function kicadSheetInstanceDesignId(sheetUuid, instancePath) {
    return `kicad_${sheetUuid}_${instancePathHash(instancePath)}`;
}
function instancePathHash(instancePath) {
    let first = 0x811c9dc5;
    let second = 0x01000193;
    for (let index = 0; index < instancePath.length; index += 1) {
        const character = instancePath.charCodeAt(index);
        first = Math.imul(first ^ character, 0x01000193);
        second = Math.imul(second ^ character, 0x85ebca6b);
    }
    return `${(first >>> 0).toString(16).padStart(8, '0')}${(second >>> 0).toString(16).padStart(8, '0')}`.slice(0, 12);
}
async function parseSchematicFiles(def, parser, warnings, projectName) {
    const parsedFiles = new Map();
    const legacySheets = [];
    for (const [schId, sch] of Object.entries(def.schematics)) {
        const contentsText = await sch.contents.text();
        if (contentsText.startsWith('EESchema')) {
            legacySheets.push({
                numberingOrder: 0,
                uuid: schId,
                filename: schId,
                name: schId,
                contents: contentsText,
                libContents: sch.lib,
                netPath: '',
            });
            continue;
        }
        const [mainSheet, ...childSheets] = (0, v6schema_1.preParseSchematicSheet)(schId, contentsText, parser, warnings, projectName);
        if (!mainSheet) {
            continue;
        }
        mainSheet.contents = contentsText;
        mainSheet.libContents = sch.lib;
        mainSheet.projectName = projectName;
        const sourceSymbolsByUuid = new Map();
        for (const childSheet of childSheets) {
            const sourceSymbols = sourceSymbolsByUuid.get(childSheet.uuid) ?? new Set();
            sourceSymbols.add(childSheet.sourceSymbolKey ?? childSheet.uuid);
            sourceSymbolsByUuid.set(childSheet.uuid, sourceSymbols);
        }
        for (const [uuid, sourceSymbols] of sourceSymbolsByUuid) {
            if (sourceSymbols.size > 1) {
                warnings.push(`Sheet ${mainSheet.filename} contains multiple sheet symbols with UUID ${uuid}; expanding each symbol`);
            }
        }
        parsedFiles.set(mainSheet.filename, { mainSheet, childSheets });
    }
    return { modernFiles: parsedFiles, legacySheets };
}
function getRootFiles(parsedFiles, projectFileName) {
    const referencedFiles = getReferencedFiles(parsedFiles);
    const roots = [...parsedFiles.entries()]
        .filter(([filename]) => !referencedFiles.has(filename))
        .map(([, file]) => file);
    const projectRoot = projectFileName ? parsedFiles.get(projectFileName.replace(/\.[^./]*$/, '.kicad_sch')) : undefined;
    if (projectRoot) {
        return [projectRoot];
    }
    if (roots.length > 0) {
        return roots;
    }
    return [...parsedFiles.entries()]
        .sort(([left], [right]) => left.localeCompare(right))
        .slice(0, 1)
        .map(([, file]) => file);
}
function getReferencedFiles(parsedFiles) {
    return new Set([...parsedFiles.values()].flatMap((file) => file.childSheets.map((sheet) => sheet.filename)));
}
function childTemplatesBySymbol(childSheets) {
    const templates = new Map();
    for (const sheet of childSheets) {
        const sourceSymbolKey = sheet.sourceSymbolKey ?? sheet.uuid;
        const entries = templates.get(sourceSymbolKey) ?? [];
        entries.push(sheet);
        templates.set(sourceSymbolKey, entries);
    }
    return [...templates.values()];
}
function selectProjectTemplates(templates, projectName) {
    const matchingTemplates = projectName ? templates.filter((template) => template.instanceProjectName === projectName) : [];
    return matchingTemplates.length > 0 ? matchingTemplates : templates;
}
function hasAncestorFile(pending, filename) {
    let current = pending;
    while (current) {
        if (current.sheet.filename === filename) {
            return true;
        }
        current = current.parent;
    }
    return false;
}
function legacySheetInstanceForPath(instances, annotationPath) {
    const legacyPath = (0, project_1.kicadInstancePathWithoutRoot)(annotationPath);
    return instances?.find((instance) => (0, project_1.joinKicadInstancePath)(instance.path, '') === legacyPath);
}
function assignSheetPages(sheets, warnings) {
    const rootPages = new Set();
    for (const sheet of sheets) {
        if (sheet.parentInstancePath) {
            continue;
        }
        const page = positivePageNumber(sheet.page) ?? positivePageNumber(sheet.numberingOrder);
        if (page) {
            rootPages.add(page);
        }
    }
    const takenPages = new Set(rootPages);
    const childSheets = sheets
        .filter((sheet) => Boolean(sheet.parentInstancePath))
        .sort((left, right) => {
        const pathOrder = (left.instancePath ?? '').localeCompare(right.instancePath ?? '');
        return pathOrder !== 0 ? pathOrder : left.filename.localeCompare(right.filename);
    });
    const validPageOwners = new Map();
    let highestPage = Math.max(0, ...rootPages);
    for (const sheet of childSheets) {
        const page = positivePageNumber(sheet.page);
        if (page) {
            highestPage = Math.max(highestPage, page);
            if (!rootPages.has(page) && !validPageOwners.has(page)) {
                validPageOwners.set(page, sheet);
            }
        }
    }
    for (const [page, sheet] of validPageOwners) {
        sheet.page = page;
        takenPages.add(page);
    }
    for (const sheet of childSheets) {
        const page = positivePageNumber(sheet.page);
        if (page && validPageOwners.get(page) === sheet) {
            continue;
        }
        const reportedPage = sheet.page ?? '<missing>';
        highestPage += 1;
        while (takenPages.has(highestPage)) {
            highestPage += 1;
        }
        sheet.page = highestPage;
        takenPages.add(highestPage);
        warnings.push(`Sheet ${sheet.filename} has duplicate or invalid page ${reportedPage}; assigned page ${highestPage}`);
    }
}
function positivePageNumber(value) {
    if (typeof value === 'number') {
        return Number.isInteger(value) && value > 0 ? value : undefined;
    }
    if (typeof value === 'string' && /^[1-9]\d*$/.test(value)) {
        return Number.parseInt(value, 10);
    }
    return undefined;
}
function schematicProjectName(filename) {
    if (!filename) {
        return undefined;
    }
    const baseName = filename.slice(filename.lastIndexOf('/') + 1);
    const extensionIndex = baseName.lastIndexOf('.');
    return extensionIndex > 0 ? baseName.slice(0, extensionIndex) : baseName;
}
//# sourceMappingURL=schematic.js.map